import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'react-toastify';
import type {
  Dialog360Environment,
  WhatsAppAccessMode,
  WhatsAppChannel,
  WhatsAppChannelPurpose,
  WhatsAppProvider,
  WhatsAppTemplate,
} from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { whatsappAdminApi } from '../api/whatsappApi';
import { WhatsAppPhoneWhitelistSection } from './WhatsAppPhoneWhitelistSection';
import { WahaLinkPanel } from './WahaLinkPanel';
import { EvoHubConnectPanel } from '@/features/evohub/components/EvoHubConnectPanel';
import { WhatsAppLocalTemplatesSection } from './WhatsAppLocalTemplatesSection';
import { CampaignTestSendPanel } from '@/features/contact-agent/components/CampaignTestSendPanel';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { hasWhatsAppProviderAccess } from '@/features/auth/moduleFeatures';
import { getPlatformAdminErrorMessage } from '@/features/platform-admin/api/platformAdminApi';

type WhatsAppChannelPanelProps = {
  instanceBotId: string;
  botName: string;
  onChannelChange?: () => void;
};

type ChannelFormState = {
  provider: WhatsAppProvider;
  phoneNumberId: string;
  displayPhoneNumber: string;
  wabaId: string;
  apiKey: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioMessagingServiceSid: string;
  accessMode: WhatsAppAccessMode;
  whitelistRejectionEnabled: boolean;
  whitelistRejectionMessage: string;
  dialog360Environment: Dialog360Environment;
  purpose: WhatsAppChannelPurpose;
};

const emptyForm: ChannelFormState = {
  provider: 'DIALOG360',
  phoneNumberId: '',
  displayPhoneNumber: '',
  wabaId: '',
  apiKey: '',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioMessagingServiceSid: '',
  accessMode: 'OPEN',
  whitelistRejectionEnabled: false,
  whitelistRejectionMessage: '',
  dialog360Environment: 'PRODUCTION',
  purpose: 'ATENCION',
};

const WAHA_ENABLED = import.meta.env.VITE_WHATSAPP_WAHA_ENABLED === 'true';
const EVOHUB_ENABLED = import.meta.env.VITE_EVOLUTION_HUB_ENABLED === 'true';

const createChannelFormSchema = z
  .object({
    provider: z.enum(['DIALOG360', 'TWILIO', 'WAHA', 'EVOHUB']),
    phoneNumberId: z.string().max(64).optional(),
    displayPhoneNumber: z.string().min(1).max(32),
    wabaId: z.string().max(64).optional(),
    apiKey: z.string().min(8).max(4096).optional(),
    twilioAccountSid: z.string().max(64).optional(),
    twilioAuthToken: z.string().min(8).max(4096).optional(),
    twilioMessagingServiceSid: z.string().max(64).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.provider === 'WAHA' || data.provider === 'EVOHUB') {
      return;
    }
    if (data.provider === 'DIALOG360') {
      if (!data.phoneNumberId?.trim()) {
        ctx.addIssue({ code: 'custom', path: ['phoneNumberId'], message: 'requerido' });
      } else if (!/^\d{5,64}$/.test(data.phoneNumberId.trim())) {
        ctx.addIssue({
          code: 'custom',
          path: ['phoneNumberId'],
          message: 'ID numérico de Meta/360dialog (no el teléfono del usuario)',
        });
      }
      if (!data.apiKey?.trim()) {
        ctx.addIssue({ code: 'custom', path: ['apiKey'], message: 'requerido' });
      }
    }
    if (data.provider === 'TWILIO') {
      if (!data.twilioAccountSid?.trim()) {
        ctx.addIssue({ code: 'custom', path: ['twilioAccountSid'], message: 'requerido' });
      }
      if (!data.twilioAuthToken?.trim()) {
        ctx.addIssue({ code: 'custom', path: ['twilioAuthToken'], message: 'requerido' });
      }
    }
  });

function providerLabel(provider: WhatsAppProvider): string {
  if (provider === 'TWILIO') return 'Twilio';
  if (provider === 'WAHA') return 'WhatsApp (número propio)';
  if (provider === 'EVOHUB') return 'Evo Hub (Meta Cloud)';
  return '360dialog';
}

function maskAccountSid(sid: string | null): string {
  if (!sid) return '—';
  if (sid.length <= 8) return '****';
  return `${sid.slice(0, 4)}…${sid.slice(-4)}`;
}

function buildTwilioPhoneNumberId(
  messagingServiceSid: string | undefined,
  displayPhoneNumber: string
): string {
  const slug = (messagingServiceSid ?? displayPhoneNumber.replace(/\D/g, '')).slice(0, 48);
  return `tw_${slug}`.slice(0, 64);
}

export function WhatsAppChannelPanel({
  instanceBotId,
  botName,
  onChannelChange,
}: WhatsAppChannelPanelProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const actorRole = currentUser?.role ?? 'USER';
  const canUse360 = hasWhatsAppProviderAccess(currentUser, 'DIALOG360');
  const canUseTwilio = hasWhatsAppProviderAccess(currentUser, 'TWILIO');
  const canUseWaha = WAHA_ENABLED && hasWhatsAppProviderAccess(currentUser, 'WAHA');
  const canUseEvoHub = EVOHUB_ENABLED && hasWhatsAppProviderAccess(currentUser, 'EVOHUB');
  const [channel, setChannel] = useState<WhatsAppChannel | null>(null);
  const [form, setForm] = useState<ChannelFormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);

  const loadChannel = useCallback(async () => {
    try {
      setLoading(true);
      const existing = await whatsappAdminApi.getChannelByInstanceBot(instanceBotId);
      setChannel(existing);
      if (existing) {
        setForm({
          provider: existing.provider,
          phoneNumberId: existing.phoneNumberId,
          displayPhoneNumber: existing.displayPhoneNumber,
          wabaId: existing.wabaId ?? '',
          apiKey: '',
          twilioAccountSid: existing.twilioAccountSid ?? '',
          twilioAuthToken: '',
          twilioMessagingServiceSid: existing.twilioMessagingServiceSid ?? '',
          accessMode: existing.accessMode,
          whitelistRejectionEnabled: existing.whitelistRejectionEnabled,
          whitelistRejectionMessage: existing.whitelistRejectionMessage ?? '',
          dialog360Environment: existing.dialog360Environment ?? 'PRODUCTION',
          purpose: existing.purpose ?? 'ATENCION',
        });
        const tpls = await whatsappAdminApi.listTemplates(existing.id);
        setTemplates(tpls);
      } else {
        setForm(emptyForm);
        setTemplates([]);
      }
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar WhatsApp');
    } finally {
      setLoading(false);
    }
  }, [instanceBotId]);

  useEffect(() => {
    loadChannel();
  }, [loadChannel]);

  useEffect(() => {
    if (channel) {
      return;
    }
    const firstProvider = (
      [
        canUse360 ? 'DIALOG360' : null,
        canUseTwilio ? 'TWILIO' : null,
        canUseEvoHub ? 'EVOHUB' : null,
        canUseWaha ? 'WAHA' : null,
      ] as const
    ).find(Boolean);
    if (firstProvider) {
      setForm(current => (current.provider === firstProvider ? current : { ...current, provider: firstProvider }));
    }
  }, [channel, canUse360, canUseTwilio, canUseEvoHub, canUseWaha]);

  const handleSave = async () => {
    if (!form.displayPhoneNumber.trim()) {
      toast.error('Completá el número visible');
      return;
    }

    try {
      setSaving(true);
      const trimmedRejection = form.whitelistRejectionMessage.trim();
      if (channel) {
        const updatePayload =
          channel.provider === 'TWILIO'
            ? {
                phoneNumberId: form.phoneNumberId || undefined,
                displayPhoneNumber: form.displayPhoneNumber,
                twilioAccountSid: form.twilioAccountSid || undefined,
                twilioAuthToken: form.twilioAuthToken || undefined,
                twilioMessagingServiceSid: form.twilioMessagingServiceSid || undefined,
                accessMode: form.accessMode,
                whitelistRejectionEnabled: form.whitelistRejectionEnabled,
                whitelistRejectionMessage: trimmedRejection || null,
                purpose: form.purpose,
              }
            : {
                phoneNumberId: form.phoneNumberId,
                displayPhoneNumber: form.displayPhoneNumber,
                wabaId: form.wabaId || undefined,
                apiKey: form.apiKey || undefined,
                dialog360Environment: form.dialog360Environment,
                accessMode: form.accessMode,
                whitelistRejectionEnabled: form.whitelistRejectionEnabled,
                whitelistRejectionMessage: trimmedRejection || null,
                purpose: form.purpose,
              };
        const updated = await whatsappAdminApi.updateChannel(channel.id, updatePayload);
        setChannel(updated);
        toast.success('Canal WhatsApp actualizado');
        onChannelChange?.();
      } else {
        const parsed = createChannelFormSchema.safeParse({
          provider: form.provider,
          phoneNumberId: form.phoneNumberId,
          displayPhoneNumber: form.displayPhoneNumber,
          wabaId: form.wabaId || undefined,
          apiKey: form.apiKey || undefined,
          twilioAccountSid: form.twilioAccountSid || undefined,
          twilioAuthToken: form.twilioAuthToken || undefined,
          twilioMessagingServiceSid: form.twilioMessagingServiceSid || undefined,
        });
        if (!parsed.success) {
          const firstIssue = parsed.error.issues[0];
          toast.error(firstIssue?.message ?? 'Datos inválidos');
          return;
        }

        const phoneNumberId =
          form.provider === 'TWILIO'
            ? buildTwilioPhoneNumberId(
                form.twilioMessagingServiceSid.trim() || undefined,
                form.displayPhoneNumber
              )
            : form.provider === 'WAHA' || form.provider === 'EVOHUB'
              ? undefined
              : form.phoneNumberId;

        const created = await whatsappAdminApi.createChannel({
          instanceBotId,
          provider: form.provider,
          phoneNumberId,
          displayPhoneNumber: form.displayPhoneNumber,
          wabaId: form.provider === 'DIALOG360' ? form.wabaId || undefined : undefined,
          apiKey: form.provider === 'DIALOG360' ? form.apiKey : undefined,
          dialog360Environment:
            form.provider === 'DIALOG360' ? form.dialog360Environment : undefined,
          twilioAccountSid: form.provider === 'TWILIO' ? form.twilioAccountSid : undefined,
          twilioAuthToken: form.provider === 'TWILIO' ? form.twilioAuthToken : undefined,
          twilioMessagingServiceSid:
            form.provider === 'TWILIO' ? form.twilioMessagingServiceSid || undefined : undefined,
          accessMode: form.accessMode,
          whitelistRejectionEnabled: form.whitelistRejectionEnabled,
          whitelistRejectionMessage: trimmedRejection || undefined,
          purpose: form.purpose,
        });
        setChannel(created);
        toast.success('Canal WhatsApp creado');
        onChannelChange?.();
      }
      setForm(current => ({ ...current, apiKey: '', twilioAuthToken: '' }));
    } catch (error) {
      toast.error(getPlatformAdminErrorMessage(error, 'Error al guardar canal'));
    } finally {
      setSaving(false);
    }
  };

  const handleHealthCheck = async () => {
    if (!channel) return;
    try {
      const result = await whatsappAdminApi.healthCheck(channel.id);
      toast.info(`Estado: ${result.status} · envío: ${result.canSendMessage ?? 'n/d'}`);
      await loadChannel();
    } catch (error) {
      toast.error((error as Error).message || 'Error en health check');
    }
  };

  const handleSyncTemplates = async () => {
    if (!channel) return;
    try {
      const result = await whatsappAdminApi.syncTemplates(channel.id);
      toast.success(`Plantillas sincronizadas: ${result.synced}`);
      const tpls = await whatsappAdminApi.listTemplates(channel.id);
      setTemplates(tpls);
    } catch (error) {
      toast.error((error as Error).message || 'Error al sincronizar plantillas');
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground py-4'>Cargando canal WhatsApp...</p>;
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle className='flex flex-wrap items-center gap-2'>
            Canal WhatsApp del bot &quot;{botName}&quot;
            {channel && (
              <Badge variant='secondary'>Provider: {providerLabel(channel.provider)}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Un canal por bot. Webhook:{' '}
            <code className='text-xs break-all'>
              {channel?.webhookUrl ?? 'configurar WHATSAPP_WEBHOOK_BASE_URL (incl. /api/webhooks/whatsapp)'}
            </code>
            {channel?.provider === 'DIALOG360' && channel.dialog360Environment === 'SANDBOX' && (
              <span className='block mt-1 text-xs'>
                Sandbox: apuntá el webhook de 360dialog a la URL anterior; no requiere
                X-Webhook-Secret.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {!channel ? (
            canUse360 || canUseTwilio || canUseEvoHub || canUseWaha ? (
            <Tabs
              value={form.provider}
              onValueChange={value => setForm({ ...form, provider: value as WhatsAppProvider })}
            >
              <TabsList>
                {canUse360 ? <TabsTrigger value='DIALOG360'>360dialog</TabsTrigger> : null}
                {canUseTwilio ? <TabsTrigger value='TWILIO'>Twilio</TabsTrigger> : null}
                {canUseEvoHub ? <TabsTrigger value='EVOHUB'>Evo Hub</TabsTrigger> : null}
                {canUseWaha ? <TabsTrigger value='WAHA'>WhatsApp (número propio)</TabsTrigger> : null}
              </TabsList>
              <TabsContent value='DIALOG360' className='space-y-4 pt-2'>
                <div className='grid gap-4 md:grid-cols-2'>
                  <div>
                    <Label>Entorno 360dialog *</Label>
                    <select
                      className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                      value={form.dialog360Environment}
                      onChange={e =>
                        setForm({
                          ...form,
                          dialog360Environment: e.target.value as Dialog360Environment,
                        })
                      }
                    >
                      <option value='PRODUCTION'>Producción (WABA real)</option>
                      <option value='SANDBOX'>Sandbox (pruebas)</option>
                    </select>
                    <p className='text-xs text-muted-foreground mt-1'>
                      Sandbox usa API y webhooks compatibles con Meta Cloud sin header
                      X-Webhook-Secret.
                    </p>
                  </div>
                  <div>
                    <Label>Phone Number ID (Meta) *</Label>
                    <Input
                      value={form.phoneNumberId}
                      onChange={e => setForm({ ...form, phoneNumberId: e.target.value })}
                      placeholder='123456789012345'
                    />
                    <p className='text-xs text-muted-foreground mt-1'>
                      ID numérico del número en Meta/360dialog Hub, no el teléfono del usuario.
                    </p>
                  </div>
                  <div>
                    <Label>Número visible *</Label>
                    <Input
                      value={form.displayPhoneNumber}
                      onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                      placeholder='5491112345678'
                    />
                  </div>
                  <div>
                    <Label>WABA ID (opcional)</Label>
                    <Input
                      value={form.wabaId}
                      onChange={e => setForm({ ...form, wabaId: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>API key 360dialog *</Label>
                    <Input
                      type='password'
                      value={form.apiKey}
                      onChange={e => setForm({ ...form, apiKey: e.target.value })}
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value='TWILIO' className='space-y-4 pt-2'>
                <div className='grid gap-4 md:grid-cols-2'>
                  <div>
                    <Label>Account SID *</Label>
                    <Input
                      value={form.twilioAccountSid}
                      onChange={e => setForm({ ...form, twilioAccountSid: e.target.value })}
                      placeholder='ACxxxxxxxx'
                    />
                  </div>
                  <div>
                    <Label>Auth Token *</Label>
                    <Input
                      type='password'
                      value={form.twilioAuthToken}
                      onChange={e => setForm({ ...form, twilioAuthToken: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Messaging Service SID (opcional)</Label>
                    <Input
                      value={form.twilioMessagingServiceSid}
                      onChange={e =>
                        setForm({ ...form, twilioMessagingServiceSid: e.target.value })
                      }
                      placeholder='MGxxxxxxxx'
                    />
                  </div>
                  <div>
                    <Label>Número visible *</Label>
                    <Input
                      value={form.displayPhoneNumber}
                      onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                      placeholder='5491112345678'
                    />
                  </div>
                </div>
                <p className='text-xs text-muted-foreground'>
                  Webhook Twilio: configurá{' '}
                  <code className='text-xs'>/api/webhooks/twilio/&lt;channelId&gt;</code> en la
                  consola de Twilio después de crear el canal.
                </p>
              </TabsContent>
              {canUseEvoHub ? (
                <TabsContent value='EVOHUB' className='space-y-4 pt-2'>
                  <div>
                    <Label>Número visible *</Label>
                    <Input
                      value={form.displayPhoneNumber}
                      onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                      placeholder='5491112345678'
                    />
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    Tras crear el canal, abrí el enlace de Evo Hub para conectar la cuenta Meta del
                    cliente.
                  </p>
                </TabsContent>
              ) : null}
              {canUseWaha ? (
                <TabsContent value='WAHA' className='space-y-4 pt-2'>
                  <div className='grid gap-4 md:grid-cols-2'>
                    <div>
                      <Label>Número visible *</Label>
                      <Input
                        value={form.displayPhoneNumber}
                        onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                        placeholder='5491112345678'
                      />
                      <p className='text-xs text-muted-foreground mt-1'>
                        Teléfono en formato internacional (solo referencia visual).
                      </p>
                    </div>
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    Después de crear el canal podés vincular el teléfono escaneando un QR.
                  </p>
                </TabsContent>
              ) : null}
            </Tabs>
            ) : (
              <p className='text-sm text-muted-foreground'>
                No hay proveedores WhatsApp habilitados para este tenant. Activá al menos uno en
                Administración → Tenants.
              </p>
            )
          ) : channel.provider === 'EVOHUB' ? (
            <div className='space-y-4'>
              <div>
                <Label>Número visible</Label>
                <Input
                  value={form.displayPhoneNumber}
                  onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                />
              </div>
              <EvoHubConnectPanel
                connectUrl={channel.evoHubConnectUrl}
                status={channel.status}
                linkedAt={channel.hubLinkedAt}
                lastError={channel.hubLastError}
              />
            </div>
          ) : channel.provider === 'WAHA' ? (
            <div className='space-y-4'>
              <div className='grid gap-4 md:grid-cols-2'>
                <div>
                  <Label>Número visible</Label>
                  <Input
                    value={form.displayPhoneNumber}
                    onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                  />
                </div>
              </div>
              <WahaLinkPanel channelId={channel.id} onLinked={() => loadChannel()} />
            </div>
          ) : channel.provider === 'TWILIO' ? (
            <div className='grid gap-4 md:grid-cols-2'>
              <div>
                <Label>Account SID</Label>
                <Input value={maskAccountSid(channel.twilioAccountSid)} readOnly disabled />
              </div>
              <div>
                <Label>Messaging Service SID</Label>
                <Input
                  value={form.twilioMessagingServiceSid}
                  onChange={e =>
                    setForm({ ...form, twilioMessagingServiceSid: e.target.value })
                  }
                  placeholder={channel.twilioMessagingServiceSid ?? '—'}
                />
              </div>
              <div>
                <Label>Número visible</Label>
                <Input
                  value={form.displayPhoneNumber}
                  onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                />
              </div>
              <div>
                <Label>Auth Token (dejar vacío para no cambiar)</Label>
                <Input
                  type='password'
                  value={form.twilioAuthToken}
                  onChange={e => setForm({ ...form, twilioAuthToken: e.target.value })}
                />
              </div>
            </div>
          ) : (
            <div className='grid gap-4 md:grid-cols-2'>
              <div>
                <Label>Entorno 360dialog</Label>
                <select
                  className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                  value={form.dialog360Environment}
                  onChange={e =>
                    setForm({
                      ...form,
                      dialog360Environment: e.target.value as Dialog360Environment,
                    })
                  }
                >
                  <option value='PRODUCTION'>Producción</option>
                  <option value='SANDBOX'>Sandbox</option>
                </select>
              </div>
              <div>
                <Label>Phone Number ID (Meta)</Label>
                <Input
                  value={form.phoneNumberId}
                  onChange={e => setForm({ ...form, phoneNumberId: e.target.value })}
                  placeholder='123456789012345'
                />
                <p className='text-xs text-muted-foreground mt-1'>
                  ID del número en Meta/360dialog, no el teléfono del cliente.
                </p>
              </div>
              <div>
                <Label>Número visible</Label>
                <Input
                  value={form.displayPhoneNumber}
                  onChange={e => setForm({ ...form, displayPhoneNumber: e.target.value })}
                  placeholder='5491112345678'
                />
              </div>
              <div>
                <Label>WABA ID (opcional)</Label>
                <Input
                  value={form.wabaId}
                  onChange={e => setForm({ ...form, wabaId: e.target.value })}
                />
              </div>
              <div>
                <Label>API key 360dialog (dejar vacío para no cambiar)</Label>
                <Input
                  type='password'
                  value={form.apiKey}
                  onChange={e => setForm({ ...form, apiKey: e.target.value })}
                  placeholder={`••••${channel.apiKeyLast4}`}
                />
              </div>
            </div>
          )}

          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label>Modo de acceso</Label>
              <select
                className='w-full border border-input rounded-md p-2 bg-background'
                value={form.accessMode}
                onChange={e =>
                  setForm({ ...form, accessMode: e.target.value as WhatsAppAccessMode })
                }
              >
                <option value='OPEN'>Abierto</option>
                <option value='WHITELIST_PHONES'>Whitelist de teléfonos</option>
              </select>
            </div>
            <div>
              <Label>Propósito</Label>
              <select
                className='w-full border border-input rounded-md p-2 bg-background'
                value={form.purpose}
                onChange={e =>
                  setForm({ ...form, purpose: e.target.value as WhatsAppChannelPurpose })
                }
              >
                <option value='ATENCION'>Atención</option>
                <option value='CONTACTO'>Contacto / cobranzas</option>
              </select>
            </div>
          </div>

          {channel && (
            <div className='text-sm text-muted-foreground space-y-1'>
              <p>Estado: {channel.status}</p>
              <p>Último health: {channel.lastHealthCheckStatus ?? 'sin datos'}</p>
            </div>
          )}

          <div className='flex flex-wrap gap-2'>
            <Button type='button' onClick={handleSave} disabled={saving}>
              {channel ? 'Guardar cambios' : 'Crear canal'}
            </Button>
            {channel && (
              <>
                <Button type='button' variant='outline' onClick={handleHealthCheck}>
                  Health check
                </Button>
                {channel.provider !== 'WAHA' ? (
                  <Button type='button' variant='outline' onClick={handleSyncTemplates}>
                    <RefreshCw className='h-4 w-4 mr-2' />
                    Sync plantillas Meta
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {channel && (
        <WhatsAppPhoneWhitelistSection
          channelId={channel.id}
          accessMode={form.accessMode}
          botLabel={botName}
          whitelistRejectionEnabled={form.whitelistRejectionEnabled}
          whitelistRejectionMessage={form.whitelistRejectionMessage}
          onRejectionChange={(enabled, message) =>
            setForm({
              ...form,
              whitelistRejectionEnabled: enabled,
              whitelistRejectionMessage: message,
            })
          }
          showRejectionSettings
          showSaveReminder
        />
      )}

      {channel && channel.provider === 'WAHA' ? (
        <Card>
          <CardHeader>
            <CardTitle>Plantillas del canal</CardTitle>
            <CardDescription>
              Mensajes de texto para campañas en este número propio (no plantillas Meta).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <WhatsAppLocalTemplatesSection
              channelId={channel.id}
              onChange={() => {
                void whatsappAdminApi.listTemplates(channel.id).then(setTemplates);
              }}
            />
          </CardContent>
        </Card>
      ) : channel ? (
        <Card>
          <CardHeader>
            <CardTitle>Plantillas ({templates.length})</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 max-h-64 overflow-y-auto'>
            {templates.length === 0 ? (
              <p className='text-sm text-muted-foreground'>Sin plantillas. Usá Sync plantillas Meta.</p>
            ) : (
              templates.map(template => (
                <div key={template.id} className='border rounded-lg p-2 text-sm'>
                  <div className='font-medium'>
                    {template.name} · {template.language}
                  </div>
                  <div className='text-muted-foreground'>
                    {template.category} · {template.status}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      ) : null}

      {channel?.purpose === 'CONTACTO' ? (
        <CampaignTestSendPanel
          actorRole={actorRole}
          channelKind='WHATSAPP'
          whatsAppChannelId={channel.id}
          tenantId={channel.tenantId}
          templates={templates
            .filter(
              item =>
                channel?.provider !== 'WAHA' ||
                (item.origin === 'LOCAL' && item.status === 'ACTIVE'),
            )
            .map(item => ({ id: item.id, name: item.name }))}
        />
      ) : null}
    </div>
  );
}
